/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import {logger, Time, Util, UUID} from 'yougile-util'
import {
  AccountChangeReq,
  AccountListReq,
  AccountListResp,
  AccountSubscribeReq,
  AccountUpReq,
  AddFeedbackTicketReq,
  Branch,
  BranchJSON,
  ChatApproveReq,
  ChatCheckSyncReq,
  ChatCheckSyncResp,
  ChatReq,
  CommitReq,
  CompanyListReq,
  CompanyListResp,
  DataObjectId,
  dataTransmitVersion,
  DeleteUserReq,
  ExtensionsUpRequest,
  GenericSocket,
  GenReq,
  GenResp,
  minorDataTransmissionVersion,
  MPAddReq,
  MPAppsResp,
  MPToggleReq,
  newsCompanyId,
  PaymentUpRequest,
  PermissionsUpRequest,
  PrivilegesReq,
  PullReq,
  ReloadReq
} from 'yougile-model'
import {Backend} from '@common/backend'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {globals} from '@extensions/globals-base'
import {DevUtils} from '../dev-utils'
import {Refresh} from '../refresh'
import {Sync} from '../sync'
import {addConnectorHandlers} from './addConnectorHandlers'
import {ClientSocket} from './client-socket'
import {ConnectorHelper} from './connector-helper'
import {ConnectorReadiness} from './connector-readiness'
import {NativeClientSocket} from './native-client-socket'
import {SyncFallback} from './sync-fallback'

const version = dataTransmitVersion
const minorVer = minorDataTransmissionVersion
const maxConnecting = 2 * Time.minute

interface SocketRequest {
  type: string
  request: GenReq
}

// todo: handle failures (network and authentication)
/**
 * This singleton object interconnects with server and ensures all the
 * information is safely delivered.
 *
 * It provides method .commit for other code to tell about local changes and
 * allows to set onBranch handler (in .init method) to process remote updates.
 */
export class Connector {
  static url: string
  static urlCheckTimer: TimeoutHandler

  static reloadRequestCount = 0
  static latestRequestTime: number
  static latestResponseTime: number
  private static onBranch: (branch: BranchJSON) => void
  static onApprove: (branchId: UUID) => void

  private static socket: GenericSocket
  static reconnectTimer: TimeoutHandler
  private static routineTimer: TimeoutHandler

  /* Indicates that authorized connection is established */
  // static ready = false
  static connectingSince = 0
  /* Stores changes that were made while connection was not established */
  static queue: Branch[] = []

  private static pendingRequests: SocketRequest[] = []
  /** reqId => callback */
  static pendingRequestCallbacks: Map<UUID, (resp: GenResp) => void> = new Map()

  static reconnect: () => void = Util.noop

  static isReady(): boolean {
    return ConnectorReadiness.ready
  }

  private static flushPendingRequests(): void {
    const requests = [...Connector.pendingRequests]
    Connector.pendingRequests = []
    requests.forEach(({type, request}: SocketRequest) => {
      Connector.emit(type, request)
    })
  }

  static request(type: 'privileges', request: PrivilegesReq): Promise<GenResp>
  static request(type: 'delete-user', request: DeleteUserReq): Promise<GenResp>
  static request(
    type: 'add-feedback-ticket',
    request: AddFeedbackTicketReq
  ): Promise<GenResp>
  static request(
    type: 'company-list',
    request: CompanyListReq
  ): Promise<CompanyListResp>
  static request(type: 'account-up', request: AccountUpReq): Promise<GenResp>
  static request(
    type: 'account-list',
    request: AccountListReq
  ): Promise<AccountListResp>
  static request(type: 'account-refresh', request: GenReq): Promise<GenResp>
  static request(
    type: 'account-subscribe',
    request: AccountSubscribeReq
  ): Promise<GenResp>
  static request(
    type: 'chat-approve',
    request: ChatApproveReq
  ): Promise<GenResp>
  static request(type: 'mp-toggle', request: MPToggleReq): Promise<GenResp>
  static request(
    type: 'mp-list' | 'mp-list-all',
    request: GenReq
  ): Promise<MPAppsResp>
  static request(
    type: 'chat-check-sync',
    request: ChatCheckSyncReq
  ): Promise<ChatCheckSyncResp>
  static request(type: 'mp-add', req: MPAddReq): Promise<GenResp>
  static request(type: 'chat', request: ChatReq): Promise<GenResp>
  static request(type: string, request: GenReq): Promise<GenResp> {
    const reqId = Util.UUID()
    const req = {...request, reqId}
    return new Promise<GenResp>((resolve, reject) => {
      Connector.pendingRequestCallbacks.set(reqId, res => {
        if (res.result === 'ok') {
          resolve(res)
        } else {
          reject(res)
        }
      })
      Connector.emit(type + '-request', req)
    })
  }

  static onRequest(
    type: 'account-change',
    cb: (req: AccountChangeReq) => void
  ): void
  static onRequest(type: 'chat', cb: (req: ChatReq) => void): void
  static onRequest(
    type: 'permissions-up',
    cb: (req: PermissionsUpRequest) => void
  ): void
  static onRequest(
    type: 'extensions-up',
    cb: (req: ExtensionsUpRequest) => void
  ): void
  static onRequest(
    type: 'payment-up',
    cb: (req: PaymentUpRequest) => void
  ): void
  static onRequest(type: 'mp-admin', cb: (req: GenReq) => void): void
  static onRequest<T extends GenReq>(type: string, cb: (req: T) => void): void {
    Connector.on(type + '-request', cb)
  }

  static emit(type: string, request: GenReq): void {
    if (globals.readonly || !request) {
      return
    }
    if (DevUtils.websocketsFailureModeTurnedOn) {
      return
    }
    const r = JSON.stringify(request)
    const sizeLimit =
      globals.standalone || globals.devMode ? 12_000_000 : 3_000_000
    if (r.length > sizeLimit) {
      Backend.report('Large emit skipped', globals.currentUserId, r.length)
      return
    }
    const {socket, pendingRequests} = Connector
    Connector.latestRequestTime = Time.now()
    if (socket?.isReady()) {
      DevUtils.delayed(2000, () => socket.emit(type, request))()
    } else {
      pendingRequests.push({type, request})
    }
  }

  static on(type: string, cb: (res: GenResp | GenReq) => void): void {
    if (DevUtils.websocketsFailureModeTurnedOn) {
      return
    }
    Connector.socket.on(type, (res: GenResp | GenReq) => {
      if ((res as GenResp).result === 'ok') {
        Connector.latestResponseTime = Time.now()
      }
      DevUtils.delayed(2000, () => cb(res))()
    })
  }

  static commit(branch: Branch): void {
    if (Connector.queue.length) {
      if (Connector.queue.find(b => b.id === branch.id)) {
        logger.log('Branch already queued')
        return
      }
    }
    if (Tracking.isActive()) {
      const str = JSON.stringify(branch)
      if (str.length >= 100000) {
        void Tracking.send(TrackingEvent.diffsBranch100K)
        if (str.length >= 500000) {
          void Tracking.send(TrackingEvent.diffsBranch500K)
        }
        if (str.length >= 1000000) {
          void Tracking.send(TrackingEvent.diffsBranch1M)
        }
        if (str.length >= 5000000) {
          void Tracking.send(TrackingEvent.diffsBranch5M)
        }
      }
    }
    if (Connector.socket && ConnectorReadiness.ready) {
      const commitReq: CommitReq = {version, branch: branch.toJSON(), minorVer}
      Connector.emit('commit-request', commitReq)
    } else {
      logger.log(
        'Not ready, queueing...',
        branch.id,
        Connector.queue.length + 1
      )
      Connector.queue.push(branch)
    }
  }

  /**
   * After data is initialized with /data/index request, this
   * method is called in order to start synchronization.
   * Further communication is available with callback “onBranch” and
   * method “commit”.
   * This method can be called multiple times, but data should be reinitialized
   * (with /data/index) each time before calling this method.
   */
  static async init({
    userId,
    key,
    onBranch,
    onApprove
  }: {
    userId: DataObjectId
    key: string
    onBranch: typeof Connector.onBranch
    onApprove: (branchId: UUID) => void
  }): Promise<void> {
    if (Connector.connectingSince) {
      logger.log('this: already connecting, skipping')
      return
    }
    SyncFallback.init()
    Connector.connectingSince = Date.now()

    Connector.cleanup()
    clearTimeout(Connector.reconnectTimer)
    Connector.onBranch = onBranch
    Connector.onApprove = onApprove

    Connector.reconnect = () => {
      void Connector.init({userId, key, onBranch, onApprove})
    }

    clearInterval(Connector.routineTimer)
    Connector.routineTimer = setInterval(
      () =>
        Util.catch(() => {
          if (
            Connector.connectingSince &&
            Date.now() - Connector.connectingSince > maxConnecting
          ) {
            logger.log('Too long connecting, reconnect')
            Connector.connectingSince = 0
            Connector.reconnect()
          }
          if (ConnectorReadiness.ready && userId !== globals.currentUserId) {
            Refresh.clearCredentials()
          }
        }),
      1000
    )

    clearInterval(Connector.urlCheckTimer)
    Connector.urlCheckTimer = setInterval(async () => {
      if (ConnectorReadiness.ready) {
        const host = await ConnectorHelper.getUrl()
        if (host !== Connector.url) {
          logger.error('Incorrect host, reconnecting', Connector.url, host)
          Connector.reconnect()
        }
      }
    }, 30_000)

    Connector.url = await ConnectorHelper.getUrl()

    Connector.socket = Backend.shouldUseNativeWs()
      ? new NativeClientSocket(Connector.url, Connector.flushPendingRequests)
      : new ClientSocket(
          new SockJS(Connector.url),
          Connector.flushPendingRequests
        )
    addConnectorHandlers(Connector.socket, userId, key)
  }

  static onReloadRequest = ({companyId}: ReloadReq): void => {
    Connector.reloadRequestCount++
    Backend.report('Received reload request', globals.currentUserId, companyId)
    if (!Backend.features.actualize) {
      void Sync.reload({
        initial: false,
        callback: () => Sync.callReload(),
        shouldUseCachedData: false,
        force: true
      })
    } else {
      void Sync.actualizeDataFromServer(companyId).then(ok => {
        if (!ok) {
          Sync.callReload(companyId)
        }
      })
    }
  }

  static checkNewCompanies(): void {
    Connector.request('company-list', {})
      .then(res => ConnectorHelper.onCompanyList(res.companies ?? []))
      .catch(res => logger.warn('company-list', res.error))
  }

  static branchReceived(branch: BranchJSON): void {
    if (!branch.fromRevision || !branch.toRevision) {
      logger.warn('No revision specified')
      return
    }
    const box = Sync.get(branch)
    if (!box) {
      if (branch.companyId === newsCompanyId) {
        return
      }
      logger.log('Unknown sandbox, companyId = ' + branch.companyId)
      Connector.checkNewCompanies()
      return
    }
    if (branch.fromRevision < box.revision) {
      // this revision is outdated, skipping...
      logger.warn(
        'Received old revision update from server',
        branch.fromRevision,
        box.revision
      )
      if (branch.toRevision > box.revision) {
        const pullReq: PullReq = {
          companyId: branch.companyId,
          revision: box.revision
        }
        setTimeout(() => Connector.emit('pull-request', pullReq), 1000)
      }
    } else if (branch.fromRevision > box.revision) {
      // the revision is from the future, we need to tell server that we need
      // some older information by making pull request
      logger.warn(
        'Received future revision from server',
        branch.fromRevision,
        box.revision
      )
      const pullReq: PullReq = {
        companyId: branch.companyId,
        revision: box.revision
      }
      Connector.emit('pull-request', pullReq)
    } else {
      // revisions match!
      Sync.get(branch).revision = branch.toRevision
      Connector.onBranch(branch)
    }
  }

  static cleanup(): void {
    if (Connector.socket) {
      try {
        Connector.socket.disconnect()
      } catch (e) {
        logger.error(e)
      }
    }
    ConnectorReadiness.ready = false
    Connector.queue = []
  }
}
